/**
 * PHARMAKON — client-side RBAC matrix
 *
 * Ported from the design's config/permissions.config.js. The server enforces
 * the authoritative copy; this is for UI gating only (hide buttons, dim menus).
 *
 * Caissier hidden fields (audit §6.3) drive both UI redaction and the
 * maskMoneyForRole() helper in lib/format.js.
 */

export const PERMISSIONS = {
  achat: {
    read: ["PROPRIETAIRE", "MANAGER"],
    create: ["PROPRIETAIRE", "MANAGER"],
    update: ["PROPRIETAIRE", "MANAGER"],
    validate: ["PROPRIETAIRE", "MANAGER"],
    delete: ["PROPRIETAIRE"],
  },
  // Refonte WP-30 — mirrors server PERMISSIONS.avoir: « Enregistrer le règlement » (and the A-02
  // « Rapprocher… ») and « Annuler l'avoir » (OP-03-10; a settled avoir: PROPRIETAIRE only —
  // `ROLES_ANNULATION_AVOIR_REGLE` of @pharmakon/shared/constants/avoirs.js).
  avoir: {
    mark_paid: ["PROPRIETAIRE", "MANAGER"],
    annuler: ["PROPRIETAIRE", "MANAGER"],
    // Refonte WP-78 (D-32) — « Confirmer l'avoir » (server PERMISSIONS.avoir.confirmer)
    confirmer: ["PROPRIETAIRE", "MANAGER"],
    // Trésorerie TWP-57 — « Imputer le crédit » of an avoir onto open factures (server PERMISSIONS.avoir.imputer)
    imputer: ["PROPRIETAIRE", "MANAGER"],
    // Refonte WP-90 — « Régler l'avoir produits » per quantity (server PERMISSIONS.avoir.resoudre)
    resoudre: ["PROPRIETAIRE", "MANAGER"],
  },
  // Refonte WP-31 — mirrors server PERMISSIONS.bon_retour (A-07: PROPRIETAIRE + MANAGER).
  bon_retour: {
    read: ["PROPRIETAIRE", "MANAGER"],
    create: ["PROPRIETAIRE", "MANAGER"],
    update: ["PROPRIETAIRE", "MANAGER"],
    delete: ["PROPRIETAIRE", "MANAGER"],
    validate: ["PROPRIETAIRE", "MANAGER"],
    annuler: ["PROPRIETAIRE", "MANAGER"],
  },
  // Refonte WP-33 — the pharmacy roles of server PERMISSIONS.bon_commande (the bilateral confirm of a
  // platform BC — « Confirmer le bon de commande », `…/commercial/bons-commande/:id/confirm`).
  bon_commande: {
    read: ["PROPRIETAIRE", "MANAGER"],
    manage: ["PROPRIETAIRE", "MANAGER"],
  },
  // Refonte WP-32 — mirrors server PERMISSIONS.bon_commande_pharmacie (the pharmacy BC lifecycle,
  // /api/v1/bons-commande: PROPRIETAIRE + MANAGER; the bilateral portal pair stays `bon_commande`).
  bon_commande_pharmacie: {
    read: ["PROPRIETAIRE", "MANAGER"],
    create: ["PROPRIETAIRE", "MANAGER"],
    update: ["PROPRIETAIRE", "MANAGER"],
    delete: ["PROPRIETAIRE", "MANAGER"],
    envoyer: ["PROPRIETAIRE", "MANAGER"],
    annuler: ["PROPRIETAIRE", "MANAGER"],
  },
  // Refonte WP-34 — mirrors server PERMISSIONS.reclamation (A-07: PROPRIETAIRE + MANAGER; the supplier
  // plane acts through fournisseur_commercial, OP-03-31).
  reclamation: {
    read: ["PROPRIETAIRE", "MANAGER"],
    create: ["PROPRIETAIRE", "MANAGER"],
    update: ["PROPRIETAIRE", "MANAGER"],
    delete: ["PROPRIETAIRE", "MANAGER"],
    envoyer: ["PROPRIETAIRE", "MANAGER"],
    repondre: ["PROPRIETAIRE", "MANAGER"],
    cloturer: ["PROPRIETAIRE", "MANAGER"],
    generer_avoir: ["PROPRIETAIRE", "MANAGER"],
  },
  vente: {
    read: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
    create: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
    update: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
    validate: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
    cancel: ["PROPRIETAIRE", "MANAGER"],
  },
  stock: {
    read: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
    correct: ["PROPRIETAIRE", "MANAGER"],
    export: ["PROPRIETAIRE", "MANAGER"],
    // X XWP-15 — « Risque de péremption » (ux/04 §2.5); mirrors server PERMISSIONS.stock.risk.
    risk: ["PROPRIETAIRE", "MANAGER"],
    // Cross-module X §14 (XD-03) — lot blocks; mirrors server PERMISSIONS.stock.bloquer.
    bloquer: ["PROPRIETAIRE", "MANAGER"],
    // Cross-module X XWP-95 / XWP-96 (XD-299) — « Invisible au comptoir » on a lot; mirrors server
    // PERMISSIONS.stock.visibilite_lot (P only).
    visibilite_lot: ["PROPRIETAIRE"],
    // Refonte WP-130 (D-51) — « Mouvements de stock » of the fiche; mirrors server PERMISSIONS.stock.mouvements.
    mouvements: ["PROPRIETAIRE", "MANAGER"],
  },
  // Cross-module X XWP-16 — the /magasins workspace; mirrors server PERMISSIONS.magasin /
  // emplacement / transfert (apps/server/src/config/constants.js). UI gating only.
  magasin: {
    read: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
    manage: ["PROPRIETAIRE", "MANAGER"],
    allocate: ["PROPRIETAIRE", "MANAGER"],
  },
  emplacement: {
    read: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
    create: ["PROPRIETAIRE", "MANAGER"],
    update: ["PROPRIETAIRE", "MANAGER"],
    delete: ["PROPRIETAIRE", "MANAGER"],
    allocate: ["PROPRIETAIRE", "MANAGER"],
  },
  transfert: {
    read: ["PROPRIETAIRE", "MANAGER"],
    create: ["PROPRIETAIRE", "MANAGER"],
    manage: ["PROPRIETAIRE", "MANAGER"],
  },
  produit: {
    read: ["PROPRIETAIRE", "MANAGER", "CAISSIER", "FOURNISSEUR", "LABORATOIRE"],
    create: ["PROPRIETAIRE", "MANAGER"],
    update: ["PROPRIETAIRE", "MANAGER"],
    delete: ["PROPRIETAIRE"],
    // Cross-module X §13 (XA-12) — « Comparer les prix d'achat »; mirrors server
    // PERMISSIONS.produit.historique_prix.
    historique_prix: ["PROPRIETAIRE", "MANAGER"],
    // Cross-module X XWP-13 — mirrors server PERMISSIONS.produit.generiques.
    generiques: ["PROPRIETAIRE", "MANAGER"],
    // Feature 1 — « Bloquer / débloquer la vente » (statut_produit); mirrors server
    // PERMISSIONS.produit.block (ADMIN_PHARMAKON passes through `can`).
    block: ["PROPRIETAIRE"],
  },
  fournisseur: {
    read: ["PROPRIETAIRE", "MANAGER"],
    create: ["PROPRIETAIRE", "MANAGER"],
    update: ["PROPRIETAIRE", "MANAGER"],
    delete: ["PROPRIETAIRE"],
    rate: ["PROPRIETAIRE", "MANAGER"],
  },
  caisse: {
    read: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
    open: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
    close: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
    // Trésorerie TWP-58 (TD-350) — mirrors server PERMISSIONS.caisse.choisir_session: name the open
    // session a return / échange / till movement lands on (GET /caisse/sessions-actives).
    choisir_session: ["PROPRIETAIRE", "MANAGER"],
  },
  // Trésorerie TWP-34 / TWP-58 — mirrors server PERMISSIONS.retour.rembourser: a cheque / virement
  // refund from a named account (the caissier refunds cash only, TD-22).
  retour: {
    rembourser: ["PROPRIETAIRE", "MANAGER"],
  },
  // X XWP-68 — the team workspace's gates mirror the server's matrices one for one (parity-tested in
  // lib/__tests__/equipe-xwp68.test.js): `user` (the MANAGER reads the list, XD-225), the per-user PIN
  // (XWP-65), the profile — activity, photo — (XWP-66), the paid seats (XWP-69) and the sales per user
  // (XWP-67). The platform admin passes `can` by its own rule, as everywhere in this mirror.
  user: {
    read: ["PROPRIETAIRE", "MANAGER"],
    create: ["PROPRIETAIRE"],
    update: ["PROPRIETAIRE"],
    delete: ["PROPRIETAIRE"],
  },
  user_pin: {
    manage: ["PROPRIETAIRE"],
    self: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
  },
  user_profil: {
    self: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
    activite_autres: ["PROPRIETAIRE"],
    photo_autres: ["PROPRIETAIRE", "MANAGER"],
    photo_gerer: ["PROPRIETAIRE"],
  },
  siege_utilisateur: {
    self: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
    equipe: ["PROPRIETAIRE", "MANAGER"],
    gerer: ["PROPRIETAIRE"],
  },
  ventes_utilisateur: {
    self: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
    autres: ["PROPRIETAIRE"],
  },
  dashboard: {
    kpis: ["PROPRIETAIRE", "MANAGER"],
    caissier: ["CAISSIER"],
  },
  // Refonte A-06 (WP-06 / WP-20) — « Achats et étiquettes » settings; mirrors server
  // PERMISSIONS.parametres_achats (read P/M, update P).
  parametres_achats: {
    read: ["PROPRIETAIRE", "MANAGER"],
    update: ["PROPRIETAIRE"],
  },
  // Cross-module X §6 · XWP-20 — the crédit journal, its drawer and dialogs; the pharmacy roles of
  // server PERMISSIONS.credit (platform admin left out, as everywhere in this mirror — `can` lets it
  // through). Pinned by apps/server/src/__tests__/credit-versement.parity.test.js.
  credit: {
    read: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
    mark_paid: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
    return_card: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
    cancel_versement: ["PROPRIETAIRE", "MANAGER"],
    versement_tresorerie: ["PROPRIETAIRE", "MANAGER"],
  },
  // Cross-module X §7 · XWP-21 — Instances CHIFA: the journal, the drawer, send / settle at the counter,
  // the corrections of a PENDING instance (modify / remove a line = `update`, delete the instance =
  // `delete`) for PROPRIETAIRE / MANAGER only (XD-17), « Envoyer vente libre » (`vente_libre`, XWP-22).
  // The pharmacy roles of server PERMISSIONS.instance, pinned by
  // apps/server/src/__tests__/instance-chifa.parity.test.js.
  instance: {
    read: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
    create: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
    send_chifa: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
    settle: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
    update: ["PROPRIETAIRE", "MANAGER"],
    delete: ["PROPRIETAIRE", "MANAGER"],
    vente_libre: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
  },
  // Cross-module X §4 · XWP-23 — the client directory: every role reads and creates (the caissier
  // creates a client from the CHIFA facture, §17); the motif and the other fields are edited by
  // PROPRIETAIRE / MANAGER (XD-21); only the owner deletes. The pharmacy roles of server
  // PERMISSIONS.client, pinned by apps/server/src/__tests__/client.parity.test.js.
  client: {
    read: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
    create: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
    update: ["PROPRIETAIRE", "MANAGER"],
    delete: ["PROPRIETAIRE"],
  },
  // Cross-module X §6.5 (XA-10) — « Caisse et crédit » settings; mirrors server
  // PERMISSIONS.parametres_caisse (read P/M, update P).
  parametres_caisse: {
    read: ["PROPRIETAIRE", "MANAGER"],
    update: ["PROPRIETAIRE"],
  },
  // Cross-module X XWP-95 / XWP-96 (XO-23) — « Lots au comptoir »; mirrors server
  // PERMISSIONS.parametres_lots_comptoir (read P/M/C — a CAISSIER reads the two counter flags only —,
  // update P under If-Match).
  parametres_lots_comptoir: {
    read: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
    update: ["PROPRIETAIRE"],
    // X XWP-98 (XO-26 (2)) — who may always move a sale line to another lot (P / M); a CAISSIER only
    // when « Le caissier choisit le lot » or « Sélectionner depuis autres lots » is on — mirrors the
    // server's `peutChangerLotLigne` (disponibilite.service.js)
    lot_ligne_toujours: ["PROPRIETAIRE", "MANAGER"],
  },
  // Refonte WP-79 (D-37, CP §7.2) — the two motif catalogues (réclamations / avoirs produits); mirrors
  // server PERMISSIONS.motifs_fournisseur (read P/M, manage P — the WP-80 settings section).
  motifs_fournisseur: {
    read: ["PROPRIETAIRE", "MANAGER"],
    manage: ["PROPRIETAIRE"],
  },
  // Refonte WP-67 (M-23; D-30) — « Paramètres › Achats et étiquettes › Types de produits »; mirrors server
  // PERMISSIONS.types_produit (read P/M/C — the pickers list the active types; manage P).
  types_produit: {
    read: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
    manage: ["PROPRIETAIRE"],
  },
  // Refonte WP-26 (A-07) — lot labels: print / reprint; mirrors server PERMISSIONS.etiquette_lot.
  etiquette_lot: {
    imprimer: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
  },
  licence: { read: ["PROPRIETAIRE"], manage: ["PROPRIETAIRE"] },
  // Refonte WP-05 (EXE-30): the pharmacy-side e-facture actions mirror the
  // server matrix (apps/server/src/config/constants.js PERMISSIONS.efacture / br).
  efacture: {
    read: ["PROPRIETAIRE", "MANAGER", "FOURNISSEUR"],
    create: ["FOURNISSEUR"],
    import: ["PROPRIETAIRE", "MANAGER"],
    validate: ["PROPRIETAIRE", "MANAGER"],
    reject: ["PROPRIETAIRE", "MANAGER"],
    modify_request: ["PROPRIETAIRE", "MANAGER"],
    pay: ["PROPRIETAIRE"],
  },
  br: {
    create: ["PROPRIETAIRE", "MANAGER"],
  },
  precommande: {
    read: ["PROPRIETAIRE", "MANAGER", "FOURNISSEUR"],
    create: ["PROPRIETAIRE", "MANAGER"],
    respond: ["FOURNISSEUR"],
  },
  catalogue: {
    read: ["FOURNISSEUR"],
    create: ["FOURNISSEUR"],
    update: ["FOURNISSEUR"],
    delete: ["FOURNISSEUR"],
  },
  admin: { all: ["ADMIN_PHARMAKON"] },
  audit: { read: ["PROPRIETAIRE", "ADMIN_PHARMAKON"] },
  // ─── Transferts inter-pharmacies (TIP, 2026-08) — réseau de confrères ─────
  // MIROIR de la matrice serveur (`config/constants.js`), pour le seul masquage
  // d'interface. Le serveur reste l'autorité : ce miroir ne donne aucun droit,
  // il évite d'afficher un bouton qui rendrait un 403.
  //
  // Nouer ou rompre une relation commerciale engage l'officine → PROPRIETAIRE
  // seul. Les documents portent des prix d'achat → le CAISSIER est exclu de
  // toute la surface.
  confrere: { manage: ["PROPRIETAIRE"] },
  transfert_inter: {
    read: ["PROPRIETAIRE", "MANAGER", "ADMIN_PHARMAKON"],
    create: ["PROPRIETAIRE", "MANAGER"],
    respond: ["PROPRIETAIRE", "MANAGER"],
    deliver: ["PROPRIETAIRE", "MANAGER"],
    settle: ["PROPRIETAIRE", "MANAGER"],
  },
  // ─── Alertes équipe (2026-08) — canal employeur → employé d'UNE officine ──
  // MIROIR de `PERMISSIONS.alerte_equipe` du serveur (`config/constants.js`),
  // pour le seul masquage d'interface. PLAN_mobile_pharma_owner_manager §4.2.
  //
  // DEUX PARTICULARITÉS À NE PAS « CORRIGER » :
  //
  //  1. `ADMIN_PHARMAKON` est ABSENT de toute la surface porteuse de contenu.
  //     Ce sont des messages internes employeur ↔ employé (« Mme X reprend la
  //     caisse 2 », « contrôle DPSP demain »), une classe de confidentialité
  //     différente du `Broadcast` plateforme : le support voit des compteurs,
  //     jamais le texte (§5.6, Q6). Le `can()` en bas de ce fichier accorde
  //     TOUT à ADMIN_PHARMAKON — il ne convient donc PAS ici, et les écrans
  //     d'alertes utilisent les gardes dédiées de
  //     `components/alertes/alerte-equipe.config.js`.
  //  2. `send_urgent` est le titulaire SEUL (Q2). Une alerte URGENTE arrête le
  //     comptoir ; la personne responsable du comptoir est la seule à pouvoir
  //     l'arrêter. Le MANAGER envoie de l'info et de l'important.
  alerte_equipe: {
    read: ["PROPRIETAIRE", "MANAGER", "CAISSIER"], // mon propre flux, auto-porté
    ack: ["PROPRIETAIRE", "MANAGER", "CAISSIER"], // accuser MA ligne
    create: ["PROPRIETAIRE", "MANAGER"],
    send: ["PROPRIETAIRE", "MANAGER"],
    send_urgent: ["PROPRIETAIRE"],
    manage: ["PROPRIETAIRE", "MANAGER"],
    read_outbox: ["PROPRIETAIRE", "MANAGER"],
    audience: ["PROPRIETAIRE", "MANAGER"],
  },
  // ─── Batch-1 vague 2 (2026-08) — SECOURS : vendre avant l'achat ────────────
  // MIROIR de `PERMISSIONS.secours` du serveur. Masquage d'interface uniquement.
  //
  // LA FRONTIÈRE EST CELLE DU STOCK, et il ne faut pas la « corriger » :
  //  · le CAISSIER vend en secours (c'est SON geste, au comptoir) et lit ses
  //    dettes ouvertes (sans quoi il vendrait à l'aveugle contre un plafond
  //    qu'il ne peut pas consulter) ;
  //  · il ne RATTACHE jamais — rattacher, c'est décider que 30 livrés valent 27
  //    disponibles, un acte de gestion de stock (plan §6, verbatim) ;
  //  · seul le PROPRIÉTAIRE régularise une dette orpheline (Q2) : solder une
  //    dette dont la marchandise n'est jamais arrivée, c'est acter une perte.
  secours: {
    read: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
    create: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
    rattacher: ["PROPRIETAIRE", "MANAGER"],
    regulariser: ["PROPRIETAIRE"],
  },
  // X XWP-55 — miroir EXACT de `PERMISSIONS.ordonnancier` du serveur (parité :
  // apps/server/src/__tests__/ordonnancier.parity.test.js). Les pièces jointes de délivrance se lisent
  // et se gèrent en PROPRIETAIRE / MANAGER seulement ; le caissier ne fait que les joindre pendant la
  // délivrance ; ADMIN_PHARMAKON n'y a jamais accès (XD-276) — `lib/ordonnancier.js` lit donc cette
  // matrice SANS le passe-droit administrateur de `can()`.
  ordonnancier: {
    read: ["ADMIN_PHARMAKON", "PROPRIETAIRE", "MANAGER"],
    correct: ["PROPRIETAIRE", "MANAGER"],
    sign: ["PROPRIETAIRE", "MANAGER"],
    print: ["ADMIN_PHARMAKON", "PROPRIETAIRE", "MANAGER"],
    bordereau_manuel: ["PROPRIETAIRE", "MANAGER"],
    pieces_read: ["PROPRIETAIRE", "MANAGER"],
    pieces_manage: ["PROPRIETAIRE", "MANAGER"],
    pieces_upload: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
  },
  // Élément 21 — « Signaler une rupture » ouvre `manquant:create` au CAISSIER.
  // Le serveur FORCE alors `type = RUPTURE_CAISSIER` : la capacité ouvre la
  // porte, le service tient le périmètre. Le journal des manquants (`read`) et
  // son traitement restent responsables+.
  manquant: {
    read: ["PROPRIETAIRE", "MANAGER"],
    create: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
    treat: ["PROPRIETAIRE", "MANAGER"],
    order: ["PROPRIETAIRE", "MANAGER"],
  },
  // Trésorerie TWP-22 (RC-130) — miroir EXACT de `PERMISSIONS.pharmacie` du serveur : la fiche de
  // l'officine, sa facturation (demandes et historique de paiement, modules) — jamais le caissier.
  pharmacie: {
    read: ["PROPRIETAIRE", "MANAGER"],
    update: ["PROPRIETAIRE"],
  },
  // ─── Batch-1 vague 3 (2026-08) — Trésorerie & Finance d'officine ──────────
  // Miroir EXACT de `server/src/config/constants.js` (PERMISSIONS). Le serveur
  // reste l'autorité ; ces lignes n'existent que pour que l'interface n'offre
  // pas un bouton qui produirait un 403 — une action visible et refusée est
  // pire qu'une action absente.
  //
  // La caisse centrale est le compte PERMANENT de l'officine : rien de tout
  // cela n'appartient au comptoir, d'où l'absence de CAISSIER — sauf l'historique
  // de ses propres opérations (TWP-07, 03_UX §12.3).
  // Trésorerie TWP-22 — miroir EXACT de `PERMISSIONS.tresorerie` du serveur : le geste du comptoir
  // (« Ma caisse ») — encaisser / décaisser au tiroir (`create`), transférer depuis SON comptoir ouvert
  // (`transfer_own`, TD-19). La portée (sa session, son comptoir) est dérivée par le serveur, jamais ici.
  tresorerie: {
    read: ["ADMIN_PHARMAKON", "PROPRIETAIRE", "MANAGER", "CAISSIER"],
    create: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
    manage: ["PROPRIETAIRE", "MANAGER"],
    solde: ["ADMIN_PHARMAKON", "PROPRIETAIRE", "MANAGER", "CAISSIER"],
    transfer_own: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
  },
  tresorerie_pharmacie: {
    read: ["PROPRIETAIRE", "MANAGER"],
    operate: ["PROPRIETAIRE", "MANAGER"],
    // Créer un emplacement de fonds, c'est ouvrir un tiroir de l'officine.
    manage: ["PROPRIETAIRE"],
    // Trésorerie TWP-05 — paramètres de trésorerie et motifs : le propriétaire règle, le
    // manager lit (TD-152).
    configure: ["PROPRIETAIRE"],
    configure_read: ["PROPRIETAIRE", "MANAGER"],
    // Trésorerie TWP-61 — ce que voit le caissier (cinq réglages) : le propriétaire et le manager (TD-347).
    visibilite_caissier: ["PROPRIETAIRE", "MANAGER"],
    // Trésorerie TWP-07 — modifier / annuler une opération confirmée : le MANAGER seulement si le
    // paramètre de l'officine le lui ouvre (TD-134, défaut non) ; le serveur tranche, et le
    // référentiel (TWP-10) servira la liste effective du rôle.
    edit_confirmed: ["PROPRIETAIRE", "MANAGER"],
    cancel_confirmed: ["PROPRIETAIRE", "MANAGER"],
    historique: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
    // Trésorerie TWP-10 — la fiche d'une opération : le caissier ses propres opérations (service).
    consulter: ["PROPRIETAIRE", "MANAGER", "CAISSIER"],
    // Trésorerie TWP-12 — le contrôle de cohérence : le propriétaire le lit et le lance ; le manager
    // le lit si le domaine « Anomalies à vérifier » lui est visible (le référentiel tranche).
    controle_read: ["PROPRIETAIRE", "MANAGER"],
    controle: ["PROPRIETAIRE"],
    // TWP-41 — compter un compte (P, M) ; passer l'écart d'un comptage en opération (P).
    comptage: ["PROPRIETAIRE", "MANAGER"],
    comptage_ecart: ["PROPRIETAIRE"],
    // TWP-13 — « Fusionner en transfert » deux en-têtes de reprise à vérifier (propriétaire seul).
    reprise: ["PROPRIETAIRE"],
    // TWP-21 — confirmer une demande née au comptoir (banque, prélèvement, transit) : jamais le caissier (TD-147).
    confirm_caissier_draft: ["PROPRIETAIRE", "MANAGER"],
  },
  charge: {
    read: ["PROPRIETAIRE", "MANAGER"],
    create: ["PROPRIETAIRE", "MANAGER"],
    update: ["PROPRIETAIRE", "MANAGER"],
    // Payer un salaire est un acte de propriétaire (plan §6) : le manager
    // saisit la charge, il ne la décaisse pas.
    pay: ["PROPRIETAIRE"],
    cancel: ["PROPRIETAIRE"],
  },
  reglement_achat: {
    read: ["PROPRIETAIRE", "MANAGER"],
    // Trésorerie TWP-24 (TD-45) — le MANAGER ne règle que si le paramètre de trésorerie de
    // l'officine le lui ouvre (défaut non) ; le serveur tranche et le référentiel sert la liste
    // effective du rôle. Sans lui, il prépare un brouillon « à valider par le propriétaire ».
    pay: ["PROPRIETAIRE", "MANAGER"],
    prepare: ["PROPRIETAIRE", "MANAGER"],
    cancel: ["PROPRIETAIRE"],
  },
};

export const CAISSIER_HIDDEN_FIELDS = [
  "prix_achat",
  "prix_revient",
  "marge",
  "marge_brute",
  "marge_nette",
  "pourcentage_marge",
  "cout_unitaire",
  "shp",
  "ttc_achat",
  "ht_achat",
];

export const can = (user, resource, action) => {
  if (!user || !user.role) return false;
  if (user.role === "ADMIN_PHARMAKON") return true;
  const allowed = PERMISSIONS?.[resource]?.[action];
  return Array.isArray(allowed) && allowed.includes(user.role);
};
